import {
  Enrolment_Bool_Exp,
  Enrolment_Order_By,
  GetEnrolmentsByBrokerQuery,
  GetEnrolmentsByBrokerQueryVariables,
  GetEnrolmentsQuery,
  GetEnrolmentsQueryVariables,
} from "@/api/hasura/generated/graphql"
import { EnrolmentParser } from "@/models/operations/enrolment/enrolment.parser"
import {
  EnrolmentBrokerType,
  EnrolmentStatusType,
  IEnrolment,
} from "@/models/operations/enrolment/enrolment.types"
import { Dayjs } from "dayjs"
import {
  Brokers,
  ENROLMENT_STATUS_COMPLETED,
  EnrolmentStatus,
  getBrokerLabel,
  JUNIFER_ENROLMENT_FAILURE_MESSAGE_REGEX,
} from "@/constants/enrolment"
import { EnrolmentSegment, EnrolmentSegmentType } from "@/types/Enrolment"
import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import { QueryResult } from "@/api/utils/types"
import { EnrolmentAutomatedCommunicationParser } from "@/models/operations/enrolmentAutomatedCommunication/enrolmentAutomatedCommunication.parser"
import { EnrolmentAutomatedCommunication } from "@/models/operations/enrolmentAutomatedCommunication/enrolmentAutomatedCommunication"

export type EnrolmentOrderBy = Enrolment_Order_By
export type EnrolmentFailureMessageFilterBy =
  Enrolment_Bool_Exp["enrolment_failure_message"]
export type EnrolmentFilters = {
  broker?: EnrolmentBrokerType[]
  createdDateFrom?: Dayjs
  createdDateTo?: Dayjs
  segment?: EnrolmentSegmentType
  onboardingStatus?: EnrolmentStatusType[]
}
export type EnrolmentQueryResult = {
  records: IEnrolment[]
  totalCount: number
}

export type BrokerCount = {
  broker: string
  count: number
}

export type EnrolmentsGroupedByBrokerQueryResult = {
  enrolmentsByBroker: Array<BrokerCount>
  totalCount: number
}

export type GetEnrolmentsParams = {
  search: string
  filters: EnrolmentFilters
  page: number
  orderBy: EnrolmentOrderBy
  pageSize?: number
}

export type GetEnrolmentsGroupedByBrokerParams = Pick<
  GetEnrolmentsParams,
  "search" | "filters"
>

export type BrokerOptions = Record<string, string>

export type BrokerFilterOptionsQueryResult = {
  brokerOptions: BrokerOptions
}

export const ONE_HOUR_IN_MS = 60 * 60 * 1000

class EnrolmentServiceClass {
  getEnrolments({
    search,
    filters,
    page,
    orderBy,
    pageSize,
  }: GetEnrolmentsParams): QueryResult<EnrolmentQueryResult | null> {
    const payload: GetEnrolmentsQueryVariables = {
      search: `%${search}%`,
      orderBy,
      ...mapGetEnrolmentsFilters(filters),
      limit: pageSize,
      offset: pageSize ? (page - 1) * pageSize : undefined,
    }

    return useQuery({
      ...HasuraQueries.enrolment.list(payload),
      useRawResponse: true,
      placeholderData: null,
      parserFn: (data: GetEnrolmentsQuery) => {
        return {
          records: data.enrolment.map((enrolment) =>
            EnrolmentParser.toDomain(enrolment),
          ),
          totalCount: data.enrolment_aggregate.aggregate?.count ?? 0,
        }
      },
    })
  }

  getEnrolmentsGroupedByBroker({
    search,
    filters,
  }: GetEnrolmentsGroupedByBrokerParams): QueryResult<EnrolmentsGroupedByBrokerQueryResult | null> {
    return useQuery({
      ...HasuraQueries.enrolment.groupedByBroker(
        mapGetEnrolmentsGroupedByBrokerFilters({ search, filters }),
      ),
      useRawResponse: true,
      staleTime: 0,
      parserFn: (data) => {
        let brokersCount = 0
        const enrolmentsByBroker = data?.enrolments_grouped_by_broker.map(
          (brokerCount) => {
            brokersCount = brokersCount + (brokerCount.count ?? 0)
            return {
              broker: Brokers[brokerCount.broker as string],
              count: brokerCount.count ?? 0,
            }
          },
        )
        return {
          enrolmentsByBroker: enrolmentsByBroker,
          totalCount: brokersCount,
        }
      },
    })
  }

  getEnrolmentAutomatedCommunicationById(
    enrolmentId: number,
  ): QueryResult<EnrolmentAutomatedCommunication[]> {
    return useQuery({
      ...HasuraQueries.enrolment.automatedCommunicationById({ enrolmentId }),
      useRawResponse: true,
      staleTime: 0,
      parserFn: (data) =>
        (data?.enrolment_automated_communications.map((communication) =>
          EnrolmentAutomatedCommunicationParser.toDomain(communication),
        ) ?? []) as EnrolmentAutomatedCommunication[],
    })
  }

  getBrokerFilterOptions({
    search,
    filters,
  }: GetEnrolmentsGroupedByBrokerParams): QueryResult<BrokerFilterOptionsQueryResult | null> {
    return useQuery({
      ...HasuraQueries.enrolment.groupedByBroker(
        mapGetEnrolmentsGroupedByBrokerFilters({ search, filters }),
      ),
      useRawResponse: true,
      staleTime: ONE_HOUR_IN_MS,
      parserFn: this.#formatBrokerFilterOptions,
    })
  }

  #formatBrokerFilterOptions(
    data: GetEnrolmentsByBrokerQuery,
  ): BrokerFilterOptionsQueryResult | null {
    const options = data?.enrolments_grouped_by_broker?.reduce(
      (acc: BrokerOptions, { broker }) => {
        if (broker) acc[broker] = getBrokerLabel(broker)
        return acc
      },
      {},
    )
    return { brokerOptions: options ?? {} }
  }
}

export const EnrolmentService = new EnrolmentServiceClass()

export function mapGetEnrolmentsFilters(filters: EnrolmentFilters) {
  let enrolmentFailureMessage: EnrolmentFailureMessageFilterBy = {}
  if (filters.segment === EnrolmentSegment.JUNIFER_ERRORS) {
    enrolmentFailureMessage = {
      _regex: JUNIFER_ENROLMENT_FAILURE_MESSAGE_REGEX,
    }
  } else if (filters.segment === EnrolmentSegment.NON_JUNIFER_ERRORS) {
    enrolmentFailureMessage = {
      _nregex: JUNIFER_ENROLMENT_FAILURE_MESSAGE_REGEX,
    }
  }
  const onboardingStatus: string[] = filters.onboardingStatus
    ? [...filters.onboardingStatus]
    : []
  if (onboardingStatus.includes(EnrolmentStatus.DONE)) {
    onboardingStatus.push(ENROLMENT_STATUS_COMPLETED)
  }

  return {
    broker: {
      _in: filters.broker?.length ? filters.broker : undefined,
    },
    createdAt: {
      _gte: filters.createdDateFrom,
      _lte: filters.createdDateTo,
    },
    onboardingStatus: {
      _in: onboardingStatus.length ? onboardingStatus : undefined,
    },
    enrolmentFailureMessage,
  }
}

export function mapGetEnrolmentsGroupedByBrokerFilters({
  search,
  filters,
}: GetEnrolmentsGroupedByBrokerParams): GetEnrolmentsByBrokerQueryVariables {
  const onboardingStatus: string[] = filters.onboardingStatus
    ? [...filters.onboardingStatus]
    : []
  if (onboardingStatus.includes(EnrolmentStatus.DONE)) {
    onboardingStatus.push(ENROLMENT_STATUS_COMPLETED)
  }

  return {
    search: `%${search}%`,
    createdAtStart: filters.createdDateFrom,
    createdAtEnd: filters.createdDateTo,
    brokers: filters.broker?.length ? filters.broker : undefined,
    enrolmentFailureMessageRegex:
      filters.segment === EnrolmentSegment.JUNIFER_ERRORS
        ? JUNIFER_ENROLMENT_FAILURE_MESSAGE_REGEX
        : undefined,
    enrolmentFailureMessageNRegex:
      filters.segment === EnrolmentSegment.NON_JUNIFER_ERRORS
        ? JUNIFER_ENROLMENT_FAILURE_MESSAGE_REGEX
        : undefined,
    onboardingStatus: onboardingStatus.length ? onboardingStatus : undefined,
  }
}
